import React, { useEffect, useState } from 'react';
import { Alert } from 'antd';
import { useIntl } from 'react-intl';
import { useNavigate } from 'react-router-dom';
import { AlertContext } from '../../context/AlertContext';
import { CloseOutlined } from '@ant-design/icons';

interface CustomAlertProps {
    customOnClose?: () => void;
}

const CustomAlert: React.FC<CustomAlertProps> = ({ customOnClose }) => {
    const {
        visible,
        setVisible,
        error,
        setError,
        warning,
        setWarning,
        successMessage,
        setSuccessMessage,
        redirectUrl,
        setRedirectUrl,
    } = React.useContext(AlertContext);

    const navigate = useNavigate();
    const intl = useIntl();

    const [message, setMessage] = useState<React.ReactNode>('');
    const [type, setType] = useState<'error' | 'warning' | 'success'>('success');

    const resetAlertState = () => {
        setError('');
        setWarning('');
        setSuccessMessage('');
        setRedirectUrl('');
        setVisible(false);
    };

    const handleClick = (e: React.MouseEvent) => {
        e.stopPropagation();
        if (redirectUrl) {
            navigate(redirectUrl);
            resetAlertState();
        }
    };

    const handleClose = (e: React.MouseEvent) => {
        e.stopPropagation();
        if (customOnClose) {
            customOnClose();
        }
        resetAlertState();
    };

    useEffect(() => {
        if (error) {
            setMessage(error);
            setType('error');
            setSuccessMessage('');
        } else if (warning) {
            setMessage(warning);
            setType('warning');
        } else if (visible) {
            setMessage(successMessage || intl.formatMessage({ id: 'success_message' }));
            setType('success');
        }
    }, [error, warning, successMessage, intl, visible]);

    if (!visible && !error && !warning) {
        return null;
    }

    return (
        <Alert
            style={{
                display: visible || error || warning ? '' : 'none',
                fontWeight: 'normal',
            }}
            className={`dentobook-alert fixed-alert clickable-alert`}
            message={message}
            type={type}
            closeIcon={<CloseOutlined />}
            onClose={handleClose}
            showIcon
            onClick={handleClick}
        />
    );
};

export default CustomAlert;